import React, { useEffect, useRef, useState } from 'react';
import { useTicketGroupEdit } from '../TicketGroupEditContext';
import { useTicketGroups } from '../TicketGroupsContext';
import Button from '@dashboard/components/Button';
import GroupCard from '@dashboard/components/GroupCard';
import { Button as ShadcnButton } from '@dashboard/components/ui/button';
import Reorderable from '@dashboard/components/Reorderable';
import TicketTypeEdit from './TicketTypeEdit';
import TicketGroupListItem from './TicketGroupListItem';
import useTicketTypeHelpers from '../useTicketTypeHelpers';
import clsx from 'clsx';
import { confirm } from '@dashboard/lib/confirm';
import { Label } from '@dashboard/components/Form';
import TicketTypesList from './TicketTypesList';
import TicketGroupLayoutForm from './TicketGroupLayoutForm';
import TicketGroupSettingsForm from './TicketGroupSettingsForm';
import { EllipsisVertical } from 'lucide-react';
import usePage from '@/dashboard/hooks/usePage';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { useTicketGroupHelpers } from '../useTicketGroupHelpers';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import { getGroupStatus, getGroupHiddenTooltip } from '../ticketGroupUtils';
import { TOOLTIP_DELAY_MS } from '@dashboard/components/ui/tooltip';

function checkCanDeleteTicketGroup(ticketGroup, page) {
  const hasSoldTicketTypes = ticketGroup.ticketTypes.some(
    ticketType => ticketType.quantitySold > 0,
  );

  if (hasSoldTicketTypes) {
    return {
      canDeleteTicketGroup: false,
      canDeleteTicketGroupMessage:
        "You can't delete this ticket group because it has made sales. You can archive it instead.",
    };
  }

  if (page.ticketGroups.length === 1) {
    return {
      canDeleteTicketGroup: false,
      canDeleteTicketGroupMessage:
        "You can't delete this group yet. Add tickets to another group first.",
    };
  }

  const ticketTypes = page.ticketGroups.flatMap(tg => tg.ticketTypes);
  const hasNoTicketTypesOutsideOfThisTicketGroup = ticketTypes.every(
    ticketType => ticketType.ticketGroup === ticketGroup._id,
  );

  if (hasNoTicketTypesOutsideOfThisTicketGroup) {
    return {
      canDeleteTicketGroup: false,
      canDeleteTicketGroupMessage:
        "You can't delete this group yet. Add tickets to another group first.",
    };
  }

  return {
    canDeleteTicketGroup: true,
    canDeleteTicketGroupMessage: '',
  };
}

function SingleTicketGroupView({
  ticketGroup,
  isAddingTicketType,
  isEditingTicketType,
  onAddTicketGroup,
  onAddTicketType,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  isNewPageFlow = false,
  usePortal = false,
}) {
  const {
    onDuplicateTicketGroup,
    onEnableDisableTicketGroup,
    onDeleteTicketGroup,
    totalQuantitySoldInGroup,
  } = useTicketGroupHelpers(ticketGroup._id);

  const { setIsAddingTicketType, setEditingTicketTypeId } = useTicketGroupEdit();

  const { page } = usePage();
  const { canDeleteTicketGroup, canDeleteTicketGroupMessage } = checkCanDeleteTicketGroup(
    ticketGroup,
    page,
  );
  const [activeTab, setActiveTab] = useState('tickets'); // 'tickets' | 'settings' | 'layout'
  const settingsSubmitRef = useRef(null);
  const layoutSubmitRef = useRef(null);

  // In new page flow, hide the ticket group wrapper and show tickets directly.
  // Keep the JSX structure stable across the 0→1 ticket transition so the
  // form below isn't unmounted/remounted when the first ticket is added.
  if (isNewPageFlow) {
    const hasTicketTypes = ticketGroup.ticketTypes.length > 0;
    return (
      <>
        <div className="flex flex-col">
          {hasTicketTypes && (
            <TicketTypesList
              ticketTypes={ticketGroup.ticketTypes}
              ticketGroupId={ticketGroup._id}
              useCardStyle
              usePortal={usePortal}
            />
          )}
        </div>

        {isAddingTicketType && (
          <div className={clsx('mb-6', hasTicketTypes && 'mt-4')}>
            <Label>New ticket</Label>
            <TicketTypeEdit ticketGroupId={ticketGroup._id} />
          </div>
        )}

        {!isAddingTicketType && !isEditingTicketType && hasTicketTypes && (
          <div className="flex items-center justify-between mt-4">
            <Button handleClick={onAddTicketGroup} appearance="secondary">
              + Add group
            </Button>
            <Button appearance="primary" onClick={() => onAddTicketType(ticketGroup._id)}>
              + Add ticket
            </Button>
          </div>
        )}
      </>
    );
  }

  if (ticketGroup.ticketTypes.length === 0) {
    return (
      <div>
        {isAddingTicketType && (
          <div className="mb-6">
            <Label>New ticket</Label>

            <TicketTypeEdit ticketGroupId={ticketGroup._id} />
          </div>
        )}
      </div>
    );
  }

  const enabledTicketTypesCount = ticketGroup.ticketTypes?.filter(
    ticketTypes => ticketTypes.status === 'enabled',
  ).length;

  const { text: statusText, badge: statusBadge } = getGroupStatus({
    ticketGroup,
    page,
    allTicketGroups: [ticketGroup],
  });
  const hiddenTooltip = getGroupHiddenTooltip({
    ticketGroup,
    page,
    allTicketGroups: [ticketGroup],
  });
  const ticketCountText = `${enabledTicketTypesCount} ticket${enabledTicketTypesCount === 1 ? '' : 's'}`;
  const capacityText =
    ticketGroup?.capacity !== null ? `${ticketGroup.capacity} available` : 'Unlimited';
  const soldText = `${totalQuantitySoldInGroup || 0} sold`;

  const tabs = [
    { key: 'tickets', label: 'Tickets' },
    { key: 'settings', label: 'Settings' },
    { key: 'layout', label: 'Layout' },
  ];

  async function handleTabChange(tab) {
    if (tab === activeTab) return;

    if (hasUnsavedChanges) {
      const proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
      if (!proceed) return;
      setHasUnsavedChanges(false);
    }

    // Cancel any ticket editing/adding when switching tabs
    setIsAddingTicketType(false);
    setEditingTicketTypeId(null);
    setActiveTab(tab);
  }

  return (
    <>
      <div
        className={clsx({
          'opacity-50': ticketGroup.status === 'disabled',
        })}
      >
        <GroupCard
          headerClassName={clsx({
            'opacity-50 pointer-events-none': ticketGroup.status === 'archived',
          })}
          title={ticketGroup.name}
          summary={
            <>
              {ticketGroup.status === 'disabled' && (
                <StatusBadge status="disabled" label="Disabled" />
              )}
              {hiddenTooltip && (
                <Tooltip label={hiddenTooltip}>
                  <StatusBadge status="hidden" label="Hidden" />
                </Tooltip>
              )}
              {statusBadge && <StatusBadge status={statusBadge.status} label={statusBadge.label} />}
              <span>
                {statusText ? `${statusText} • ` : ''}
                {ticketCountText} • {capacityText} • {soldText}
              </span>
            </>
          }
          actions={
            <div className="shrink-0 flex items-center pr-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <ShadcnButton
                    variant="ghost"
                    size="icon"
                    aria-label="More options"
                    disabled={isAddingTicketType || ticketGroup.status === 'archived'}
                  >
                    <EllipsisVertical className="size-4" />
                  </ShadcnButton>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={onDuplicateTicketGroup}>Duplicate</DropdownMenuItem>
                  <DropdownMenuItem onSelect={onEnableDisableTicketGroup}>
                    {ticketGroup.status === 'enabled' ? 'Disable' : 'Enable'}
                  </DropdownMenuItem>

                  {canDeleteTicketGroup ? (
                    <DropdownMenuItem onSelect={onDeleteTicketGroup} variant="destructive">
                      Delete
                    </DropdownMenuItem>
                  ) : (
                    <RadixTooltip.Provider delayDuration={TOOLTIP_DELAY_MS}>
                      <RadixTooltip.Root>
                        <RadixTooltip.Trigger asChild>
                          <DropdownMenuItem
                            disabled
                            onSelect={e => e.preventDefault()}
                            className="opacity-50"
                          >
                            Delete
                          </DropdownMenuItem>
                        </RadixTooltip.Trigger>

                        <RadixTooltip.Content
                          className={clsx(
                            'data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none leading-none will-change-[transform,opacity]',
                            'inline-flex items-center rounded px-1.5 py-1.5 mx-2',
                            'bg-black dark:bg-gray-800 shadow z-50',
                          )}
                          side="left"
                        >
                          <RadixTooltip.Arrow className="fill-current text-black dark:text-gray-800 mx-auto" />
                          <span className="block text-sm text-white dark:text-gray-100 max-w-xs text-left">
                            {canDeleteTicketGroupMessage}
                          </span>
                        </RadixTooltip.Content>
                      </RadixTooltip.Root>
                    </RadixTooltip.Provider>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          }
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />

        {/* Settings tab - outside group border */}
        {activeTab === 'settings' && (
          <div className="mt-2">
            <div className="pt-2 mb-6">
              <TicketGroupSettingsForm
                ticketGroup={ticketGroup}
                onClose={() => {
                  setHasUnsavedChanges(false);
                  setActiveTab('tickets');
                }}
                formSubmit={({ handleSubmit }) => {
                  settingsSubmitRef.current = handleSubmit;
                  return null;
                }}
              />

              <div className="mt-4 flex items-center justify-end gap-x-4">
                {hasUnsavedChanges && (
                  <div className="text-gray-600 dark:text-muted-foreground text-sm">
                    Changes not saved
                  </div>
                )}
                <Button
                  appearance="secondary"
                  onClick={() => {
                    setHasUnsavedChanges(false);
                    setActiveTab('tickets');
                  }}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={() => {
                    settingsSubmitRef.current?.();
                  }}
                  type="button"
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Design tab - outside group border */}
        {activeTab === 'layout' && (
          <div className="mt-2">
            <div className="pt-2 mb-6">
              <TicketGroupLayoutForm
                ticketGroup={ticketGroup}
                onClose={() => {
                  setHasUnsavedChanges(false);
                  setActiveTab('tickets');
                }}
                formSubmit={({ handleSubmit }) => {
                  layoutSubmitRef.current = handleSubmit;
                  return null;
                }}
              />

              <div className="mt-4 flex items-center justify-end gap-x-4">
                {hasUnsavedChanges && (
                  <div className="text-gray-600 dark:text-muted-foreground text-sm">
                    Changes not saved
                  </div>
                )}
                <Button
                  appearance="secondary"
                  onClick={() => {
                    setHasUnsavedChanges(false);
                    setActiveTab('tickets');
                  }}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={() => {
                    layoutSubmitRef.current?.();
                  }}
                  type="button"
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tickets tab - outside group border */}
        {activeTab === 'tickets' && (
          <div className="mt-2">
            <div className="flex flex-col">
              <TicketTypesList
                ticketTypes={ticketGroup.ticketTypes}
                ticketGroupId={ticketGroup._id}
                useCardStyle
                usePortal={usePortal}
              />
            </div>

            {isAddingTicketType && (
              <div className="mt-4 mb-6">
                <Label>New ticket</Label>
                <TicketTypeEdit ticketGroupId={ticketGroup._id} />
              </div>
            )}

            {!isAddingTicketType && !isEditingTicketType && (
              <div className="mt-4 mb-4 flex justify-end">
                <Button appearance="primary" onClick={() => onAddTicketType(ticketGroup._id)}>
                  + Add ticket
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {!isAddingTicketType && !isEditingTicketType && activeTab === 'tickets' && (
        <div>
          <Button handleClick={onAddTicketGroup} appearance="secondary">
            + Add group
          </Button>
        </div>
      )}
    </>
  );
}

export default function TicketGroupsList({ isNewPageFlow = false, usePortal = false }) {
  const { state: ticketGroups } = useTicketGroups();
  const {
    onReorderTicketGroups,
    isAddingTicketType,
    isEditingTicketType,
    onAddTicketGroup,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    editingTicketGroupMode,
  } = useTicketGroupEdit();

  const { onAddTicketType } = useTicketTypeHelpers();

  const enabledTicketGroupsCount = ticketGroups.filter(
    ticketGroup => ticketGroup.status === 'enabled',
  ).length;

  // Automatically open forms when no tickets exist
  useEffect(() => {
    if (ticketGroups.length === 0) {
      onAddTicketGroup();
      return;
    }

    if (enabledTicketGroupsCount === 1 && ticketGroups.length === 1) {
      const ticketGroup = ticketGroups[0];
      if (ticketGroup.ticketTypes.length === 0 && !isAddingTicketType && !isEditingTicketType) {
        onAddTicketType();
      }
    }
  }, [
    ticketGroups.length,
    enabledTicketGroupsCount,
    ticketGroups,
    onAddTicketGroup,
    onAddTicketType,
    isAddingTicketType,
    isEditingTicketType,
  ]);

  if (ticketGroups.length === 0) {
    return null;
  }

  if (enabledTicketGroupsCount === 1 && ticketGroups.length === 1) {
    const ticketGroup = ticketGroups[0];

    return (
      <SingleTicketGroupView
        ticketGroup={ticketGroup}
        isAddingTicketType={isAddingTicketType}
        isEditingTicketType={isEditingTicketType}
        onAddTicketGroup={onAddTicketGroup}
        onAddTicketType={onAddTicketType}
        hasUnsavedChanges={hasUnsavedChanges}
        setHasUnsavedChanges={setHasUnsavedChanges}
        isNewPageFlow={isNewPageFlow}
        usePortal={usePortal}
      />
    );
  }

  return (
    <div>
      <div>
        <Reorderable.Wrapper>
          <Reorderable
            id="ticketGroups"
            items={ticketGroups}
            onReorder={onReorderTicketGroups}
            usePortal={usePortal}
            renderItem={({ item: ticketGroup, dragHandleProps, isDragging }) => (
              <Reorderable.Item isDragging={isDragging} border={false}>
                <TicketGroupListItem
                  ticketGroup={ticketGroup}
                  dragHandleProps={dragHandleProps}
                  isNewPageFlow={isNewPageFlow}
                  hasMultipleGroups={ticketGroups.length > 1}
                  isLast={ticketGroups[ticketGroups.length - 1]?._id === ticketGroup._id}
                  usePortal={usePortal}
                />
              </Reorderable.Item>
            )}
          />
        </Reorderable.Wrapper>
      </div>

      {!isAddingTicketType &&
        !isEditingTicketType &&
        editingTicketGroupMode !== 'settings' &&
        editingTicketGroupMode !== 'layout' && (
          <div className="flex gap-x-2">
            <Button handleClick={onAddTicketGroup} appearance="secondary">
              + Add group
            </Button>
          </div>
        )}
    </div>
  );
}
